import React from 'react';
import { Link } from 'react-router-dom';
import MixtapeLogo from '../ui/MixtapeLogo';
import { openConsentPreferences } from '../consent/consentClient';
import FOOTER from '../../content/footerLinks.json';

/**
 * The site footer, shared by every public and legal page.
 *
 * The landing page and the partners page previously each carried their own
 * inline copy of this, and they had drifted: the partners one was missing the
 * refund policy link and the "Consent Preferences" button. That button is how
 * visitors reopen the consent manager, so it needs to be reachable from every
 * page the consent engine runs on, not just the homepage.
 *
 * Layout follows the brand book footer (Figma "04 · Patterns", node 68:50):
 * cream ground under a heavy ink rule, the lockup and tagline on the left, then
 * Product / Partners / Company columns, and a legal line underneath.
 *
 * Figma also lists "Template Catalogue", "Pricing" and "Referral Program". None
 * of those has a public page yet, so they are left out rather than linked to
 * nothing — add them here when the pages exist.
 *
 * Deliberately not rendered inside the signed-in app (dashboard, editor,
 * payment, download) — those are a focused workspace rather than a marketing
 * surface.
 */
// The words and links live in content/footerLinks.json, which scripts/prerender.mjs
// also reads to write this footer into every public page's served HTML.
export const ABN = FOOTER.abn;

const LINK = 'text-brand-ink hover:underline underline-offset-4 decoration-2';

const COLUMNS = FOOTER.columns;

const Footer = () => (
  <footer className="bg-brand-rose border-t-4 border-brand-ink text-sm">
    <div className="mx-auto w-full max-w-[1240px] px-5 sm:px-8 pt-10 pb-8">
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to="/" className="inline-flex" aria-label="Mixtape Memories home">
            <MixtapeLogo size={20} decorative />
          </Link>
          <p className="mt-4 text-brand-ink">{FOOTER.tagline}</p>
          {/* Our official profiles; the same list is the Organization's sameAs in
              public/index.html. rel="me" marks them as ours. */}
          <p className="mt-3 text-brand-ink">
            Follow us:{' '}
            {FOOTER.social.map((s, i) => (
              <React.Fragment key={s.href}>
                {i > 0 && <>{' '}<span aria-hidden="true">·</span>{' '}</>}
                <a href={s.href} rel="me noopener" className={LINK}>{s.label}</a>
              </React.Fragment>
            ))}
          </p>
        </div>

        {COLUMNS.map(col => (
          <div key={col.heading}>
            <h2 className="font-sans font-bold text-sm text-brand-ink">{col.heading}</h2>
            <ul className="mt-3 space-y-2">
              {col.links.map(l => (
                <li key={l.to}><Link to={l.to} className={LINK}>{l.label}</Link></li>
              ))}
              {col.heading === 'Company' && (
                <li>
                  <button
                    type="button"
                    onClick={openConsentPreferences}
                    className={`${LINK} bg-transparent border-0 p-0 cursor-pointer text-left`}
                  >
                    Consent Preferences
                  </button>
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-10 pt-6 border-t border-brand-taupe/50 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-label text-brand-ink">
        <p>&copy; {new Date().getFullYear()} Mixtape Memories. All rights reserved. ABN {ABN}</p>
        <p>{FOOTER.country}</p>
      </div>
    </div>
  </footer>
);

export default Footer;
